import React, { useCallback, useEffect, useMemo, useState } from "react";

import { faTrashAlt } from "@fortawesome/pro-duotone-svg-icons";
import {
  faCompressAlt,
  faExpandAlt,
  faPlus,
  faTimes,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import {
  deleteAttachment as deleteAttachmentAction,
  setSetting,
  updateAttachment as updateAttachmentAction,
  uploadRequest as uploadRequestAction,
  createPlaceholderAttachment as createPlaceholderAttachmentAction,
} from "actions";

import { AttachmentForm } from "components/AttachmentForm";
import { ConfirmBrandUpdateDialog } from "components/AttachmentForm/ConfirmBrandUpdateDialog";
import { MultiButton, SlimButton } from "components/Button";
import Clickable from "components/Button/Clickable";
import Dialog from "components/Dialog";
import { Button } from "components/Form/Button";
import { Column, Row } from "components/Layout";
import { Paper } from "components/Paper";
import { UploadModal } from "components/UploadModal";

import { AttachmentSearchFields } from "constants/attachmentSearch";

import { Settings } from "constants/settings";

import { CurrentAgencySelector } from "containers/ConnectedSelector";

import { compareAttachments } from "lib/compare";
import {
  getLivestockSaleId,
  getSaleyardAuctionRoute,
  getSaleyardName,
} from "lib/navigation";

import {
  AttachmentsViewItem,
  currentSaleSelector,
  getConsignments,
  getFileById,
  getProperties,
  getSetting,
  selectDeploymentSaleIdByLivestockSaleIdAndAgencyId,
  selectNvdAttachmentItems,
} from "selectors";

import {
  getCurrentAgenciesOptions,
  selectAgencyIdByDeploymentSaleIdLookup,
} from "selectors/agencies";

import {
  useHasAddAttachmentWithoutImagePermission,
  useMountEffect,
} from "hooks";
import { useIsMobile } from "hooks/useScreenSize";
import { useHistory } from "react-router-dom";

import { AttachmentSearch } from "./AttachmentSearch";
import AttachmentThumb from "./AttachmentThumb";
import { Preview } from "./Preview";
import { PDFPreview } from "components/BillingWorkFlow/PDFPreview";
import UnusedEnvdForm from "./UnusedEnvdForm";
import MessageBox from "components/MessageBox";

const Wrapper = styled(Column)`
  ${({ theme }) => `
  flex: 1;
  overflow-y: hidden;
  height: calc(100vh - 63px - ${theme.space[2] * 2}px);
`}
`;

const ModalWrapper = styled(Column)`
  background-color: #fff;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
`;

const AddAttachmentContainer = styled.div`
  position: absolute;
  top: 0;
  right: 12px;
  bottom: 0;
  display: flex;
  align-items: center;
`;

const MobileButton = styled(Clickable)`
  display: flex;
  align-items: center;
  color: #666;
  font-size: 24px;
  ${({ theme, hoverRed }) =>
    hoverRed && `&:hover{ color: ${theme.colors.inputError} }`};
`;

const CloseModalButton = styled(Clickable)`
  position: fixed;
  right: 36px;
  bottom: 36px;
  font-size: 24px;
  color: #666;
  line-height: 0;
  padding: 12px;
  border-radius: 100px;
  background-color: #fff;
`;

const ThumbnailRow = styled.div`
  position: relative;
  min-height: 78px;
  border-bottom: 1px solid #bababa;
`;

const ThumbnailRowInner = styled(Row)`
  padding: 0 12px;
  margin-right: 72px;
  overflow-x: auto;
`;

const ThumbnailColumn = styled(Column)`
  flex: 1;
  overflow-y: auto;
  align-items: center;
`;

const MobileControls = styled(Row)`
  justify-content: space-between;
  padding: 24px;
  ${({ disabled }) => {
    if (disabled) {
      return `opacity: 0.33;
              pointer-events: none;`;
    }
  }};
`;

const FilterBar = styled(Row)`
  ${({ theme }) => `
  padding: ${theme.space[1]}px ${theme.space[2]}px;
  gap: ${theme.space[2]}px;
  align-items: center;
`}
`;

const AgencySelectorWrapper = styled.div`
  flex-shrink: 0;
`;

const SearchWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const CloseButton = styled(Clickable)(
  ({ theme }) => `
  font-size: ${theme.fontSizes.delta}px;
`,
);

const DesktopGridLayout = styled.div`
  ${({ theme }) => `
  flex: 1;
  display: grid;
  grid-gap: ${theme.space[2]}px;
  margin: 0 ${theme.space[2]}px ${theme.space[2]}px;
  grid-template-columns: min-content auto 328px;
  min-height: 0;
  > div {
    overflow: hidden;
  }
`}
`;

const FormWrapper = styled(Paper)`
  position: relative;
  min-height: 0;
  overflow: hidden;
`;

type SearchFilters = Record<string, unknown[]>;

function getFilteredAttachments(
  attachmentViewItems: AttachmentsViewItem[],
  agencyId,
  deploymentSaleId,
  attachmentSearchFilters: SearchFilters,
  properties,
) {
  return attachmentViewItems
    .filter((item: AttachmentsViewItem) => {
      // Agency / deployment sale filter
      if (agencyId && deploymentSaleId) {
        if (
          item.deploymentSaleId &&
          item.deploymentSaleId !== deploymentSaleId
        ) {
          return false;
        }
      }

      // Attachment search filters
      if (attachmentSearchFilters) {
        for (const [field, values] of Object.entries(attachmentSearchFilters)) {
          if (!values || values.length === 0) {
            continue;
          }

          if (field === AttachmentSearchFields.Type) {
            if (!values.includes(item.documentType)) {
              return false;
            }
          } else if (field === AttachmentSearchFields.Consignment) {
            if (
              !item.consignment?.id ||
              !values.includes(item.consignment?.id)
            ) {
              return false;
            }
          } else if (field === AttachmentSearchFields.SerialNumber) {
            if (!item.serialNumber || !values.includes(item.serialNumber)) {
              return false;
            }
          } else if (field === AttachmentSearchFields.VendorPIC) {
            if (!item.vendorPropertyId) {
              return false;
            }
            const property = properties[item.vendorPropertyId];
            if (!property?.PIC || !values.includes(property.PIC)) {
              return false;
            }
          } else if (field === AttachmentSearchFields.AdditionalPICs) {
            if (!item.consignment) {
              return false;
            }
            const { consignment } = item;
            const additionalPICs = (
              consignment?.additional_properties || []
            ).map(p => p?.PIC);
            if (!values.some((v: string) => additionalPICs.includes(v))) {
              return false;
            }
          } else if (field === AttachmentSearchFields.NVDVersion) {
            if (!item.nvdVersion) {
              return false;
            }
            if (!item.nvdVersion || !values.includes(item.nvdVersion)) {
              return false;
            }
          } else if (field === AttachmentSearchFields.IsComplete) {
            if (item.isComplete === null || !values.includes(item.isComplete)) {
              return false;
            }
          }
        }
      }

      return true;
    })
    .sort(compareAttachments);
}

const AttachmentsView = () => {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const history = useHistory();

  const redirectToConsignments = () => {
    const saleyard = getSaleyardName();
    const saleId = getLivestockSaleId();
    history.push(`${getSaleyardAuctionRoute(saleyard, saleId)}/consignments`);
  };

  const sale = useSelector(currentSaleSelector);

  const hasAddAttachmentWithoutImagePermission =
    useHasAddAttachmentWithoutImagePermission();

  const attachmentsViewItems: AttachmentsViewItem[] = useSelector(
    selectNvdAttachmentItems,
  );
  const consignments = useSelector(getConsignments);
  const properties = useSelector(getProperties);

  // Read agency from Redux setting
  const agencyId = useSelector(getSetting(Settings.agencyId));
  const agencyOptions = useSelector(getCurrentAgenciesOptions(false));

  const deploymentSaleId = useSelector((state: State) =>
    (
      selectDeploymentSaleIdByLivestockSaleIdAndAgencyId as (
        state: State,
        livestockSaleId: number,
        agencyId: number,
      ) => number
    )(state, getLivestockSaleId(), agencyId),
  );

  // Read attachment search filters from Redux setting
  const attachmentSearchFilters = useSelector(
    getSetting(Settings.attachmentSearch),
  );

  const selectedAttachmentIdSetting = useSelector(
    getSetting(Settings.selectedAttachmentId),
  );

  const filteredAttachmentItems = useMemo(
    () =>
      getFilteredAttachments(
        attachmentsViewItems,
        agencyId,
        deploymentSaleId,
        attachmentSearchFilters,
        properties,
      ),
    [
      attachmentsViewItems,
      agencyId,
      deploymentSaleId,
      attachmentSearchFilters,
      properties,
    ],
  );

  const [selectedAttachmentItemId, setSelectedAttachmentItemId] = useState(
    selectedAttachmentIdSetting ||
      (filteredAttachmentItems[0] ? filteredAttachmentItems[0].id : null),
  );

  const selectedAttachmentItem = useMemo(
    () => filteredAttachmentItems.find(a => a.id === selectedAttachmentItemId),
    [filteredAttachmentItems, selectedAttachmentItemId],
  );

  const attachment: Attachment = useSelector(
    getFileById(selectedAttachmentItemId),
  );

  // Clear one-shot selected attachment on mount; clear all filters on unmount
  useMountEffect(() => {
    if (selectedAttachmentIdSetting) {
      dispatch(setSetting(Settings.selectedAttachmentId, null));
    }
    return () => {
      dispatch(setSetting(Settings.attachmentSearch, null));
      dispatch(setSetting(Settings.selectedAttachmentId, null));
    };
  });

  const [confirmBrandUpdateDialogProps, setConfirmBrandUpdateDialogProps] =
    useState(null);

  useEffect(() => {
    if (
      !selectedAttachmentItemId ||
      !filteredAttachmentItems.find(a => a.id === selectedAttachmentItemId)
    ) {
      setSelectedAttachmentItemId(
        filteredAttachmentItems[0] ? filteredAttachmentItems[0].id : null,
      );
    }
  }, [filteredAttachmentItems, selectedAttachmentItemId]);

  const [isImageModal, setIsImageModal] = useState(false);
  const [isUploadModal, setIsUploadModal] = useState(false);
  const [isDeleteDialog, setIsDeleteDialog] = useState(false);

  const getNextAttachment = () => {
    const currentIndex = filteredAttachmentItems.findIndex(
      a => a.id === selectedAttachmentItemId,
    );
    const nextAttachmentIdem = filteredAttachmentItems[currentIndex + 1];
    if (nextAttachmentIdem) {
      setSelectedAttachmentItemId(nextAttachmentIdem.id);
    }
  };

  const deleteSelectedAttachment = () => {
    const attachmentItem = filteredAttachmentItems.find(
      ai =>
        ai.id === selectedAttachmentItemId &&
        (ai.type === "Scanned Image" || ai.type === "eNVD"),
    );
    if (attachmentItem) {
      dispatch(deleteAttachmentAction(attachment));
      getNextAttachment();
    }
  };

  const replaceSelectedAttachment = (
    selectedAttachment: Attachment,
    newFile,
  ) => {
    dispatch(updateAttachmentAction({ ...selectedAttachment, file: newFile }));
  };

  const agencyIdByDeploymentSaleId = useSelector(
    selectAgencyIdByDeploymentSaleIdLookup,
  );

  // Derive consignment filter state
  const consignmentFilter =
    attachmentSearchFilters?.[AttachmentSearchFields.Consignment];
  const selectedConsignmentIdFromFilter =
    consignmentFilter?.length === 1 ? consignmentFilter[0] : null;
  const hasMultipleConsignments = consignmentFilter?.length > 1;

  // Determine effective agency: use selected agency, or auto-use if only one option
  const hasAgencyFilter = Boolean(agencyId);
  const onlyAgencyId =
    agencyOptions.length === 1 ? agencyOptions[0].value : null;
  const effectiveAgencyId = agencyId || onlyAgencyId;

  // Check for agency/consignment conflict
  const selectedConsignment = selectedConsignmentIdFromFilter
    ? consignments[selectedConsignmentIdFromFilter]
    : null;
  const consignmentAgencyId = selectedConsignment
    ? agencyIdByDeploymentSaleId[selectedConsignment.deployment_sale]
    : null;
  const hasAgencyConflict =
    hasAgencyFilter &&
    selectedConsignmentIdFromFilter &&
    consignmentAgencyId &&
    consignmentAgencyId !== agencyId;

  const handleUpload = useCallback(
    (file: Attachment) => {
      const formData = { sale, consignment: undefined, agency: undefined };

      if (selectedConsignmentIdFromFilter) {
        formData.consignment = selectedConsignmentIdFromFilter;
      }
      if (effectiveAgencyId) {
        formData.agency = effectiveAgencyId;
      }
      dispatch(uploadRequestAction(file, formData));
      setIsUploadModal(false);
    },
    [
      setIsUploadModal,
      selectedConsignmentIdFromFilter,
      effectiveAgencyId,
      sale,
      dispatch,
    ],
  );

  const thumbnails = filteredAttachmentItems.map(attachmentItem => {
    return (
      <AttachmentThumb
        key={attachmentItem.id}
        id={attachmentItem.id}
        parseResult={attachmentItem.parseResult}
        progress={attachmentItem.progress}
        thumbUrl={attachmentItem.thumbnailUrl}
        selected={selectedAttachmentItemId === attachmentItem.id}
        setSelected={setSelectedAttachmentItemId}
        isComplete={attachmentItem.isComplete}
        isMobile={isMobile}
        isEnvd={
          attachmentItem.type === "eNVD" ||
          attachmentItem.type === "Unused eNVD"
        }
      />
    );
  });

  const DeleteDialog = () =>
    isDeleteDialog && (
      <Dialog
        closeSelf={() => setIsDeleteDialog(false)}
        handleSubmit={deleteSelectedAttachment}
        title="Permanently delete document?"
        subtitle="Are you sure you want to permanently delete the file?"
        warningText="The document data entered will also be deleted"
        submitButtonText="Delete Document"
        rejectButtonText="Keep"
        icon={faTrashAlt}
      />
    );

  const UploadFileModal = () =>
    isUploadModal && (
      <UploadModal
        closeSelf={() => setIsUploadModal(false)}
        handleUpload={handleUpload}
      />
    );

  const handleGoBack = () => redirectToConsignments();

  const handleShowUploadModal = () => setIsUploadModal(true);

  const handleCreateEmptyAttachment = () => {
    const formData = { sale, consignment: undefined, agency: undefined };

    if (selectedConsignmentIdFromFilter) {
      formData.consignment = selectedConsignmentIdFromFilter;
    }
    if (effectiveAgencyId) {
      formData.agency = effectiveAgencyId;
    }
    dispatch(createPlaceholderAttachmentAction(formData));
  };

  // Build a label describing what the new attachment will be created for
  const effectiveAgencyLabel = effectiveAgencyId
    ? agencyOptions.find(o => o.value === effectiveAgencyId)?.label
    : null;
  const addContext = selectedConsignmentIdFromFilter
    ? "consignment"
    : effectiveAgencyLabel
      ? effectiveAgencyLabel
      : null;

  // Enable add when we have enough context: an agency or a single consignment
  // Disable when multiple consignments filtered, or agency/consignment conflict
  const canAdd =
    !hasMultipleConsignments &&
    !hasAgencyConflict &&
    (hasAgencyFilter || onlyAgencyId || selectedConsignmentIdFromFilter);
  const addDisabledTooltip = hasAgencyConflict
    ? "Selected consignment does not belong to the selected agency"
    : !canAdd
      ? "Select an agency or a single consignment to add attachments"
      : undefined;

  const addTooltip =
    addDisabledTooltip || (addContext ? `Adding to ${addContext}` : undefined);

  const attachmentActions = [
    {
      title: isMobile ? null : "Add",
      isDisabled: !canAdd,
      tooltip: addTooltip,
      onClick: handleShowUploadModal,
      default: true,
      icon: faPlus,
      dataTour: "addAttachment",
    },
    {
      title: "Add without image",
      isDisabled: !hasAddAttachmentWithoutImagePermission || !canAdd,
      tooltip: addTooltip,
      onClick: handleCreateEmptyAttachment,
      default: false,
      icon: faPlus,
      dataTour: "addAttachmentWithoutImage",
    },
  ];

  if (isMobile) {
    const thumbnailsAndAddNew = (
      <ThumbnailRow>
        <ThumbnailRowInner alignCenter>{thumbnails}</ThumbnailRowInner>
        <AddAttachmentContainer>
          {hasAddAttachmentWithoutImagePermission ? (
            <MultiButton buttons={attachmentActions} />
          ) : (
            <Button
              onClick={handleShowUploadModal}
              disabled={!canAdd}
              title={addTooltip}
              minWidth="2.5rem"
            >
              <FontAwesomeIcon icon={faPlus} />
            </Button>
          )}
        </AddAttachmentContainer>
      </ThumbnailRow>
    );

    if (isImageModal) {
      return (
        <ModalWrapper>
          {thumbnailsAndAddNew}

          <Preview
            file={attachment}
            handleDelete={deleteSelectedAttachment}
            handleReplace={replaceSelectedAttachment}
          />

          <CloseModalButton onClick={() => setIsImageModal(false)}>
            <FontAwesomeIcon icon={faCompressAlt} />
          </CloseModalButton>
        </ModalWrapper>
      );
    }

    return (
      <Wrapper isMobile>
        <FilterBar style={{ flexDirection: "column", alignItems: "stretch" }}>
          <Row alignCenter justifyBetween>
            <AgencySelectorWrapper>
              <CurrentAgencySelector includeAll />
            </AgencySelectorWrapper>
            <CloseButton onClick={handleGoBack}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
          </Row>
          <SearchWrapper>
            <AttachmentSearch />
          </SearchWrapper>
        </FilterBar>
        {thumbnailsAndAddNew}
        <MobileControls disabled={selectedAttachmentItemId === null}>
          <MobileButton onClick={() => setIsImageModal(true)}>
            <FontAwesomeIcon icon={faExpandAlt} />
            <span style={{ fontSize: 14, paddingLeft: 12 }}>View document</span>
          </MobileButton>
          <MobileButton hoverRed onClick={() => setIsDeleteDialog(true)}>
            <FontAwesomeIcon icon={faTrashAlt} />
          </MobileButton>
        </MobileControls>
        <FormWrapper>
          {selectedAttachmentItem?.type === "Unused eNVD" && (
            <UnusedEnvdForm
              key={selectedAttachmentItem.id}
              eNvdId={selectedAttachmentItemId}
              candidateDeclarationId={
                selectedAttachmentItem.candidateDeclarationId
              }
              getNextAttachment={getNextAttachment}
            />
          )}
          {(selectedAttachmentItem?.type === "Scanned Image" ||
            selectedAttachmentItem?.type === "eNVD") && (
            <AttachmentForm
              key={selectedAttachmentItemId}
              attachmentItem={selectedAttachmentItem}
              getNextAttachment={getNextAttachment}
              handleGoBack={handleGoBack}
            />
          )}
        </FormWrapper>
        <DeleteDialog />
        <UploadFileModal />
        <ConfirmBrandUpdateDialog
          confirmBrandUpdateDialogProps={confirmBrandUpdateDialogProps}
          setConfirmBrandUpdateDialogProps={setConfirmBrandUpdateDialogProps}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <FilterBar>
        <AgencySelectorWrapper>
          <CurrentAgencySelector includeAll />
        </AgencySelectorWrapper>
        <SearchWrapper>
          <AttachmentSearch />
        </SearchWrapper>
        <CloseButton onClick={handleGoBack}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
      </FilterBar>
      <DesktopGridLayout>
        <Paper spacing={1} style={{ minWidth: 108 }}>
          <ThumbnailColumn>{thumbnails}</ThumbnailColumn>
          <Row justifyCenter style={{ marginTop: 12, marginBottom: 6 }}>
            {hasAddAttachmentWithoutImagePermission ? (
              <MultiButton
                buttons={attachmentActions}
                popoverHorizontal="left"
              />
            ) : (
              <Button
                onClick={handleShowUploadModal}
                disabled={!canAdd}
                title={addTooltip}
              >
                Add <FontAwesomeIcon icon={faPlus} />
              </Button>
            )}
          </Row>
        </Paper>
        {selectedAttachmentItem?.type === "Unused eNVD" && (
          <React.Fragment key={selectedAttachmentItem.id}>
            <div className="flex">
              <PDFPreview url={selectedAttachmentItem.pdfUrl} />
            </div>
            <FormWrapper>
              <UnusedEnvdForm
                eNvdId={selectedAttachmentItemId}
                candidateDeclarationId={
                  selectedAttachmentItem.candidateDeclarationId
                }
                getNextAttachment={getNextAttachment}
              />
            </FormWrapper>
          </React.Fragment>
        )}
        {(selectedAttachmentItem?.type === "Scanned Image" ||
          selectedAttachmentItem?.type === "eNVD") && (
          <>
            <Preview
              file={attachment}
              handleDelete={deleteSelectedAttachment}
              handleReplace={replaceSelectedAttachment}
            />
            <FormWrapper>
              <AttachmentForm
                // Force remount when switching between attachments or when candidate declaration changes. Makes sure the user gets the suggestions ASAP
                key={`${selectedAttachmentItemId}_${selectedAttachmentItem.candidateDeclarationId}`}
                attachmentItem={selectedAttachmentItem}
                getNextAttachment={getNextAttachment}
                handleGoBack={handleGoBack}
                setConfirmBrandUpdateDialogProps={
                  setConfirmBrandUpdateDialogProps
                }
              />
            </FormWrapper>
          </>
        )}
        {!selectedAttachmentItemId && (
          <MessageBox>
            <Column>
              No attachment selected
              <SlimButton onClick={handleGoBack}>Go back</SlimButton>
            </Column>
          </MessageBox>
        )}
      </DesktopGridLayout>
      <UploadFileModal />
      <ConfirmBrandUpdateDialog
        confirmBrandUpdateDialogProps={confirmBrandUpdateDialogProps}
        setConfirmBrandUpdateDialogProps={setConfirmBrandUpdateDialogProps}
      />
    </Wrapper>
  );
};

export default AttachmentsView;
